import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import EditIcon from "@mui/icons-material/Edit";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import { Box, Button, Chip, Stack } from "@mui/material";
import { useMemo, useState } from "react";

import {
  type ModuleQuestionDto,
  ModuleQuestionType,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useModuleQuestionsQuery } from "Modules/Api/Queries/useModuleQuestionsQuery";
import {
  type BlDetailSectionItem,
  DetailSection,
} from "Shared/Sections/DetailSection";
import { EditModuleQuestionsModal } from "./EditModuleQuestionsModal";

interface ModuleQuestionsSectionProps {
  moduleID: string | undefined;
}

export const ModuleQuestionsSection = ({
  moduleID,
}: ModuleQuestionsSectionProps): React.ReactNode => {
  const { t } = useResource();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { data: questions, isLoading } = useModuleQuestionsQuery(moduleID);

  const items: BlDetailSectionItem[] | undefined = useMemo(
    () =>
      questions?.map((q) => ({
        id: q.moduleQuestionID,
        startAdornment: (
          <DetailSection.IconAdornment>
            {questionTypeIcon[q.type]}
          </DetailSection.IconAdornment>
        ),
        label: {
          text: q.text,
        },
        endAdornment: <QuestionEndAdornment question={q} />,
      })),
    [questions],
  );

  return (
    <Box>
      <DetailSection
        title={t(Resources.Modules.Questions.Title)}
        action={
          <Button
            variant="text"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setIsEditOpen(true)}
            sx={{ fontSize: "16px", textTransform: "none" }}
          >
            {t(Resources.Modules.Questions.EditQuestions)}
          </Button>
        }
        items={items}
        isLoading={isLoading}
        emptyMessage={t(Resources.Modules.Questions.NoQuestions)}
      />

      {moduleID && (
        <EditModuleQuestionsModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          moduleID={moduleID}
          existingQuestions={questions ?? []}
        />
      )}
    </Box>
  );
};

const QuestionEndAdornment = ({
  question,
}: {
  question: ModuleQuestionDto;
}): React.ReactNode => {
  const { t } = useResource();

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={(theme) => ({
        "& .MuiChip-root": {
          fontSize: 13,
          padding: theme.spacing(0.75, 2),
          height: "auto",
        },
      })}
    >
      {question.isRequired && (
        <Chip
          label={t(Resources.Modules.Questions.Required)}
          size="small"
          color="error"
          variant="outlined"
        />
      )}
      <Chip
        label={t(Resources.Modules.Questions.Types[question.type])}
        size="small"
        color={questionTypeColor[question.type]}
      />
    </Stack>
  );
};

const questionTypeColor: Record<
  ModuleQuestionType,
  "default" | "primary" | "secondary" | "info"
> = {
  [ModuleQuestionType.Text]: "info",
  [ModuleQuestionType.SingleChoice]: "secondary",
  [ModuleQuestionType.SingleChoiceWithOther]: "secondary",
  [ModuleQuestionType.MultiChoice]: "primary",
  [ModuleQuestionType.MultiChoiceWithOther]: "default",
};

const questionTypeIcon: Record<ModuleQuestionType, React.ReactNode> = {
  [ModuleQuestionType.Text]: <EditNoteOutlinedIcon fontSize="small" />,
  [ModuleQuestionType.SingleChoice]: (
    <RadioButtonCheckedOutlinedIcon fontSize="small" />
  ),
  [ModuleQuestionType.SingleChoiceWithOther]: (
    <RadioButtonCheckedOutlinedIcon fontSize="small" />
  ),
  [ModuleQuestionType.MultiChoice]: <CheckBoxOutlinedIcon fontSize="small" />,
  [ModuleQuestionType.MultiChoiceWithOther]: (
    <CheckBoxOutlinedIcon fontSize="small" />
  ),
};
